import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Country, City } from "country-state-city";
import { Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "../assets/css/Rfq.css";
import aiStart from "../assets/sound/ai-start.mp3";
import aiDone from "../assets/sound/ai-done.mp3";
import aiWait from "../assets/sound/ai-wait.mp3";
import aiError from "../assets/sound/ai-error.mp3";
import {
  FileText,
  Folder,
  Grid2X2Icon,
  AlignVerticalSpaceAround,
  Upload,
} from "lucide-react";
import type { AIResponseType, DocumentItem, RequestType } from "../types/rfq";
import {
  getFieldIcon,
  initialState,
  NON_NEGATIVE_FIELDS,
  step1,
  stepDescriptions,
  steps,
  wizardConfig,
} from "../config/rfqConfig";
import { fillRfqWithAI, getRfqById, submitRfq, updateRfq } from "../services/Rfq/rfqService";
import RfqActionBar from "../components/Rfq/RfqActionBar";
import RfqFieldRenderer from "../components/Rfq/RfqFieldRenderer";
import RfqHeader from "../components/Rfq/RfqHeader";
import RfqSteps from "../components/Rfq/RfqSteps";
import RfqSummary from "../components/Rfq/RfqSummary";
import { useTranslate } from "../hooks/LanguageProvider";

export default function DemandeOffre() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(id) && location.pathname.includes("/rfq/edit/");
  const isReadMode = Boolean(id) && !isEditMode;
  const isCreateMode = !id;
  const [currentStep, setCurrentStep] = useState(1);
  const [activeCertifField, setActiveCertifField] = useState<string | null>(null);
  const [request, setRequest] = useState<RequestType>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFillingAI, setIsFillingAI] = useState(false);
  const [isLoadingRfq, setIsLoadingRfq] = useState(false);
  const { translate } = useTranslate();


  const normalizeRfqPayload = (data: any): RequestType => {
    const meta = data?.meta || {};
    const documents = Array.isArray(data?.documents)
      ? data.documents.map((doc: any) => ({
          ...doc,
          file: {
            name: doc.original_name || doc.stored_name || doc.file_path || "document",
          } as any,
          type: doc.doc_type || doc.type || "",
        }))
      : [];

    return {
      ...initialState,
      ...data,
      segment: data?.segment_label || data?.segment || "",
      request_type: meta.request_type || data?.request_type || [],
      cities: meta.city || meta.cities || data?.cities || [],
      logistics: meta.logistics || data?.logistics || [],
      criteria: meta.criteria || data?.criteria || [],
      certifications:
        meta.certification || meta.certifications || data?.certifications || [],
      documents,
    } as RequestType;
  };

  useEffect(() => {
    if (!id) {
      setRequest(initialState);
      setCurrentStep(1);
      return;
    }

    const loadRfq = async () => {
      try {
        setIsLoadingRfq(true);
        const response = await getRfqById(id);
        const payload = response.data?.data || response.data;

        if (!payload) {
          toast.error("RFQ introuvable", {
            position: "top-right",
            autoClose: 2500,
          });
          return;
        }

        setRequest(normalizeRfqPayload(payload));
        setErrors({});
      } catch (error) {
        console.error("Erreur chargement RFQ:", error);
        toast.error("Erreur lors du chargement du RFQ.", {
          position: "top-right",
          autoClose: 2500,
        });
      } finally {
        setIsLoadingRfq(false);
      }
    };

    loadRfq();
  }, [id, location.pathname]);

  const playSound = (src: string, volume = 0.18) => {
    const audio = new Audio(src);
    audio.volume = volume;
    audio.play().catch(() => {});
  };

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries()
        .map((country) => ({
          value: country.isoCode,
          label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "fr")),
    []
  );

  const citySuggestions = useMemo(() => {
    if (!request.country) return [];

    return (City.getCitiesOfCountry(String(request.country).toUpperCase()) || [])
      .map((city) => city.name)
      .filter((city, index, arr) => arr.indexOf(city) === index)
      .sort((a, b) => a.localeCompare(b, "fr"))
      .slice(0, 500);
  }, [request.country]);

  const validateStep = (step: number) => {
    const nextErrors: Record<string, string> = {};

    if (step === 1) {
      if (!request.title?.trim()) nextErrors.title = "Titre obligatoire";
      if (!request.category) nextErrors.category = "Catégorie obligatoire";
      if (!request.segment?.trim()) nextErrors.segment = "Segment obligatoire";

      if (!Array.isArray(request.request_type) || request.request_type.length === 0) {
        nextErrors.request_type = "Sélectionne au moins un type de demande";
      }
    }

    if (step === 2) {
      if (!request.start_date) nextErrors.start_date = "Date démarrage obligatoire";
      if (!request.deadline) nextErrors.deadline = "Date limite offre obligatoire";
      if (!request.delivery_date) {
        nextErrors.delivery_date = "Date 1ère livraison obligatoire";
      }
      if (!request.country) nextErrors.country = "Pays fournisseur obligatoire";

      if (request.start_date && request.delivery_date) {
        if (new Date(request.delivery_date) < new Date(request.start_date)) {
          nextErrors.delivery_date =
            "La date de livraison doit être après la date de démarrage";
        }
      }
    }

    if (step === 4) {
      if (!request.visibility) nextErrors.visibility = "Visibilité obligatoire";
      if (!request.response_deadline) {
        nextErrors.response_deadline = "Date limite réponse obligatoire";
      }

      if (request.deadline && request.response_deadline) {
        if (new Date(request.response_deadline) < new Date(request.deadline)) {
          nextErrors.response_deadline =
            "La date limite réponse doit être après la date limite offre";
        }
      }
    }

    return nextErrors;
  };

  const handleChange = (name: string, value: any) => {
    if (isReadMode) return;

    let nextValue = value;

    if (NON_NEGATIVE_FIELDS.includes(name)) {
      if (nextValue === "" || nextValue === null || nextValue === undefined) {
        nextValue = "";
      } else {
        nextValue = Math.max(0, Number(nextValue));
      }
    }

    setRequest((prev) => ({
      ...prev,
      [name]: nextValue,
      ...(name === "country" ? { cities: [] } : {}),
    }));

    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (name === "country") delete next.cities;
      return next;
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadMode) return;

    const files = Array.from(e.target.files || []) as File[];
    if (!files.length) return;

    setRequest((prev) => ({
      ...prev,
      documents: [
        ...prev.documents,
        ...files.map((file) => ({
          file,
          type: "",
        })),
      ],
    }));

    e.target.value = "";
  };

  const removeFile = (index: number) => {
    if (isReadMode) return;

    setRequest((prev) => ({
      ...prev,
      documents: prev.documents.filter((_: DocumentItem, i: number) => i !== index),
    }));
  };

  const updateDocumentType = (index: number, type: string) => {
    if (isReadMode) return;

    setRequest((prev) => ({
      ...prev,
      documents: prev.documents.map((doc: DocumentItem, i: number) =>
        i === index ? { ...doc, type } : doc
      ),
    }));
  };

  const notifyValidationError = (nextErrors: Record<string, string>) => {
    if (Object.keys(nextErrors).length === 0) return false;

    setErrors(nextErrors);
    toast.error(Object.values(nextErrors)[0], {
      position: "top-right",
      autoClose: 2500,
    });
    return true;
  };

  const goNext = () => {
    if (isReadMode) {
      if (currentStep < steps.length) setCurrentStep((s) => s + 1);
      return;
    }

    const nextErrors = validateStep(currentStep);
    if (notifyValidationError(nextErrors)) return;

    if (currentStep < steps.length) {
      setCurrentStep((s) => s + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleStepClick = (targetStep: number) => {
    if (isReadMode) {
      setCurrentStep(targetStep);
      return;
    }

    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }

    const nextErrors = validateStep(currentStep);
    if (notifyValidationError(nextErrors)) return;

    setCurrentStep(targetStep);
  };

  const handleSubmit = async () => {
    if (isReadMode) return;

    const nextErrors = validateStep(currentStep);
    if (notifyValidationError(nextErrors)) return;

    try {
      setIsSubmitting(true);

      if (isEditMode && id) {
        await updateRfq(id, request);
        toast.success("RFQ modifié avec succès", {
          position: "top-right",
          autoClose: 2500,
        });
        navigate(`/rfq/${id}`);
        return;
      }

      await submitRfq(request);

      toast.success("Votre demande a été publiée avec succès", {
        position: "top-right",
        autoClose: 2500,
      });
      navigate("/rfqs");
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors) {
        const firstError = Object.values(apiErrors)[0];
        toast.error(String(firstError), {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      toast.error("Erreur lors de l'enregistrement.", {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmptyValue = (value: any) => {
    if (value === undefined || value === null) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    return false;
  };

  const handleFillWithAI = async () => {
    if (isReadMode) {
      toast.info("Passez en mode modification pour utiliser l’AI.", {
        position: "top-right",
        autoClose: 2200,
      });
      return;
    }

    const description = request.description?.trim();

    if (!description) {
      setErrors((prev) => ({
        ...prev,
        description: "La description est obligatoire pour utiliser l’AI",
      }));

      playSound(aiError, 0.2);

      toast.error("Remplissez d’abord la description.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    try {
      playSound(aiStart, 0.15);

      setIsFillingAI(true);

      setTimeout(() => {
        playSound(aiWait, 0.1);
      }, 300);

      const aiResult = await fillRfqWithAI(description);
      const aiData: AIResponseType = aiResult?.data ?? aiResult ?? {};

      const usableEntries = Object.entries(aiData).filter(
        ([key, value]) =>
          key !== "documents" &&
          value !== undefined &&
          value !== null &&
          value !== ""
      );

      if (usableEntries.length === 0) {
        playSound(aiError, 0.2);

        toast.error("Aucune donnée exploitable trouvée.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      setRequest((prev) => {
        const next = { ...prev };

        usableEntries.forEach(([key, aiValue]) => {
          const currentValue = (prev as any)[key];

          if (isEmptyValue(currentValue)) {
            (next as any)[key] = aiValue;
          }
        });

        return next;
      });

      playSound(aiDone, 0.2);

      toast.success("Le formulaire a été pré-rempli avec AI.", {
        position: "top-right",
        autoClose: 2500,
      });
    } catch (error) {
      console.error("AI fill error:", error);

      playSound(aiError, 0.25);

      toast.error("Échec du remplissage automatique.", {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setIsFillingAI(false);
    }
  };

  const completion = useMemo(() => {
    const fields = wizardConfig[currentStep];
    const total = fields.length;

    const filled = fields.filter((field) => {
      const value = request[field.id];
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && String(value).trim() !== "";
    }).length;

    return Math.round((filled / total) * 100);
  }, [currentStep, request]);

  const summaryItems = [
    {
      icon: <FileText size={14} />,
      label: "title_request",
      value: request.title || translate("not_provided"),
    },
    {
      icon: <Folder size={14} />,
      label: "category",
      value:
        step1.find((f) => f.id === "category")?.options?.find((o: any) => o.value === request.category)
          ?.label
          ? translate(
              step1.find((f) => f.id === "category")?.options?.find((o: any) => o.value === request.category)
                ?.label as string
            )
          : translate("not_provided"),
    },
    {
      icon: <Grid2X2Icon size={14} />,
      label: "segment",
      value: request.segment || translate("not_provided"),
    },
    {
      icon: <AlignVerticalSpaceAround size={14} />,
      label: "sourcing_type",
      value: request.sourcing_type ? translate(request.sourcing_type) : translate("not_provided"),
    },
    {
      icon: <Upload size={14} />,
      label: "documents",
      value:
        request.documents?.length > 0
          ? `${request.documents.length} fichier(s)`
          : translate("no_documents"),
    },
  ];

  return (
    <div className="container-fluid mt-4 aida-bg">
      <ToastContainer />

      <RfqHeader
        title={
          isCreateMode
            ? translate(steps[currentStep - 1].title)
            : isEditMode
              ? `Modifier RFQ #${id}`
              : `Lecture RFQ #${id}`
        }
        description={translate(stepDescriptions[currentStep])}
        isFillingAI={isFillingAI || isLoadingRfq}
        onFillWithAI={handleFillWithAI}
      />

      <RfqSteps
        steps={steps}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />

      <div className="rfq-layout">
        <div>
          <div className="aida-card">
            <Row>
              {wizardConfig[currentStep].map((field) => (
                <Col md={field.col || 6} key={field.id} className="field-col">
                  <div className="label-inline">
                    {getFieldIcon(field.id)}
                    <span>{translate(field.label)}</span>
                  </div>

                  <RfqFieldRenderer
                    field={field}
                    request={request}
                    errors={errors}
                    countryOptions={countryOptions}
                    citySuggestions={citySuggestions}
                    activeCertifField={activeCertifField}
                    setActiveCertifField={setActiveCertifField}
                    onChange={isReadMode ? () => {} : handleChange}
                    onFileUpload={isReadMode ? () => {} : handleFileUpload}
                    onRemoveFile={isReadMode ? () => {} : removeFile}
                    onUpdateDocumentType={isReadMode ? () => {} : updateDocumentType}
                    onToastError={(message) =>
                      toast.error(message, {
                        position: "top-right",
                        autoClose: 2200,
                      })
                    }
                  />
                </Col>
              ))}
            </Row>
          </div>
        </div>

        <div>
          <RfqSummary
            completion={completion}
            summaryItems={summaryItems}
            currentStep={currentStep}
            steps={steps}
          />
        </div>
      </div>

      {isReadMode ? (
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => navigate("/rfqs")}
          >
            Retour
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate(`/rfq/edit/${id}`)}
          >
            Modifier
          </button>
        </div>
      ) : (
        <RfqActionBar
          currentStep={currentStep}
          totalSteps={steps.length}
          isSubmitting={isSubmitting}
          onPrev={goPrev}
          onNextOrSubmit={currentStep === steps.length ? handleSubmit : goNext}
        />
      )}
    </div>
  );
}